import type { Doc, Json } from '@w/crdt'

export type PageValue = null | boolean | number | string | readonly PageValue[] |
  { readonly [key: string]: PageValue }
export type TextSegment = { readonly id: string; readonly text: string }
export type PageBlock = { readonly id: string; readonly type: string; readonly [key: string]: PageValue }
export type PageBlocks = readonly PageBlock[]
export type PageDocument = { readonly content: { readonly children: PageBlocks }; readonly title?: string }
export type NewBlock = { type: string; [key: string]: Json }

/** The Doc owns identity and freezing; validate the page boundary without copying its view. */
/** Frozen views already checked: a crdt view never changes, so its shape is checked once, not on every read. */
const checkedPages = new WeakSet<object>()
export function pageDocument(doc: Pick<Doc, 'toJSON'>): PageDocument {
  const value = doc.toJSON()
  if (value && typeof value === 'object' && checkedPages.has(value)) return value as unknown as PageDocument
  if (!value || typeof value !== 'object' || Array.isArray(value) ||
    !('content' in value) || !value.content || typeof value.content !== 'object' ||
    Array.isArray(value.content) || !Array.isArray(value.content.children) ||
    !value.content.children.every(block => isBlock(block))) throw new Error('Expected page content.children')
  if (Object.isFrozen(value) && Object.isFrozen(value.content) && Object.isFrozen(value.content.children)) checkedPages.add(value)
  return value as unknown as PageDocument
}

export interface BlockLocation {
  block: PageBlock
  path: string
  list: string
  index: number
  parent: string
  field: string
}

/** A built index: locations by id in document (pre-)order, plus each id's place in that order. */
interface BuiltIndex {
  map: Map<string, BlockLocation>
  order: BlockLocation[]
  position: Map<string, number>
}

const viewIndexes = new WeakMap<object, BuiltIndex>()
/** Locations that follow a block's location in document order and belong to its subtree. */
const subtreeSizes = new WeakMap<BlockLocation, number>()
let lastIndexed: BuiltIndex | undefined

/**
 * `indexBlocks` of a CRDT view (`Doc.toJSON()`, `ViewDelta.before/after`), computed once per view object: a view is a
 * deep-frozen projection that is replaced, never mutated, when the document changes. Callers only read the index.
 * A new view is derived from the last indexed view (cbe-core-index-incremental): `crdt` `project` keeps the identity of
 * unchanged containers, so a block object found at the same path keeps its location and its whole subtree's
 * locations, copied from the previous order without visiting the subtree. Any two views give a correct result; related
 * ones make it cheap.
 */
export function indexViewBlocks(view: PageValue | Readonly<Json> | undefined): ReadonlyMap<string, BlockLocation> {
  if (!view || typeof view !== 'object' || !Object.isFrozen(view)) return indexBlocks(view ?? null)
  let index = viewIndexes.get(view)
  if (!index) {
    index = build(view, lastIndexed)
    viewIndexes.set(view, index)
  }
  lastIndexed = index
  return index.map
}

let indexPasses = 0
let locationsBuilt = 0

/** Block locations allocated since load (full and derived indexes): the per-change work behind cbe-core-index-incremental. */
export function blockLocationsBuilt(): number {
  return locationsBuilt
}

/** Whole-document `indexBlocks` walks since load: the work counter behind cbe-core-index-per-version. */
export function blockIndexPasses(): number {
  return indexPasses
}

/** Includes captions, question bodies and other nested block fields, not only children. */
export function indexBlocks(root: PageValue): Map<string, BlockLocation> {
  indexPasses++
  return build(root).map
}

/**
 * Walks `root`, but a block that `previous` holds with the same object at the same path is not visited: its location
 * (rebuilt only when its list index shifted) and its subtree's locations are copied from `previous`. Work is the
 * changed blocks' own fields plus the lists on their paths, plus one pointer copy per location.
 */
function build(root: PageValue, previous?: BuiltIndex): BuiltIndex {
  const map = new Map<string, BlockLocation>()
  const order: BlockLocation[] = []
  const position = new Map<string, number>()
  const add = (location: BlockLocation): void => {
    const id = location.block.id
    if (map.has(id)) throw new Error(`Duplicate block identity ${id}`)
    map.set(id, location)
    position.set(id, order.length)
    order.push(location)
  }
  const visit = (value: PageValue, path: string, parent: string, field: string): void => {
    if (isArray(value)) {
      for (const [index, item] of value.entries()) {
        if (isBlock(item)) {
          const blockPath = `${path}.${item.id}`
          const old = previous?.map.get(item.id)
          if (previous && old && old.block === item && old.path === blockPath) {
            const size = subtreeSizes.get(old) ?? 0
            const kept = old.index === index && old.parent === parent && old.field === field && old.list === path
            const location = kept ? old : { block: item, path: blockPath, list: path, index, parent, field }
            if (!kept) {
              locationsBuilt++
              subtreeSizes.set(location, size)
            }
            add(location)
            const start = (previous.position.get(item.id) ?? 0) + 1
            for (let next = start; next < start + size; next++) add(previous.order[next])
            continue
          }
          const location = { block: item, path: blockPath, list: path, index, parent, field }
          locationsBuilt++
          add(location)
          const start = order.length
          visit(item, blockPath, item.id, '')
          subtreeSizes.set(location, order.length - start)
        } else visit(item, `${path}.[${index}]`, parent, `${field}.${index}`)
      }
    } else if (value && typeof value === 'object') {
      for (const [key, item] of Object.entries(value)) {
        visit(item, path ? `${path}.${key}` : key, parent, key)
      }
    }
  }
  visit(root, '', 'root', '')
  return { map, order, position }
}

function isBlock(value: unknown): value is PageBlock {
  return value !== null && typeof value === 'object' && !Array.isArray(value) &&
    'id' in value && typeof value.id === 'string' && 'type' in value && typeof value.type === 'string'
}

function isArray(value: PageValue): value is readonly PageValue[] {
  return Array.isArray(value)
}
